import { bindColumnProps } from '@/cascade/bindColumnProps';
import { closeSubtree } from '@/cascade/cascadeReducer';
import { isDraftColumnId, type CascadeColumn, type CascadeStack } from '@/cascade/types';
import { idPropOf, sidebars, type SidebarType } from '@/registries/sidebars';
import type { Sidebar } from '@/stores/sidebarStore';
import { draftDefinition, isDraftDirty } from '@/stores/draftDefinitions';
import { activeDraftKey, type DraftKey } from '@/stores/draftKey';

/**
 * Is this cascade column a draft right now, and under which key? (TM-9455 / ADR-0029.)
 *
 * One rule, three readers: the column paints its rail from it, the panel header shows
 * its badge from it, and the store draws the draft boundary from it. They were three
 * copies, and they drifted — the column kept only the generational half, so a
 * questionnaire opened under a patient stayed grey when collapsed while its own header
 * showed the badge.
 *
 * Two kinds of draft answer here (§G / §G-bis):
 *
 * - **Identified**: the column carries a real record id and `keyFor` builds a key from
 *   its bound props, ancestors included — a questionnaire is "this form for this
 *   patient", so the patient comes from the `patientFile` column above it.
 * - **Generational**: the column carries the `new` sentinel (or a later generation) and
 *   the type answers for its single active create slot.
 *
 * A type can be BOTH (`customFormDetail` creates new forms and edits saved ones), so a
 * null identity key falls through to the generational question rather than ending it.
 */
export function columnDraftKey(stack: CascadeStack, column: CascadeColumn): DraftKey | null {
  const definition = draftDefinition(column.type);
  if (!definition?.keyFor) return null;
  return definition.keyFor(
    bindColumnProps(
      stack,
      column,
      sidebars.find((sidebar) => sidebar.type === column.type)?.inline,
    ),
  );
}

/**
 * The draft an OVERLAY panel owns, from the props its open carried (§C-ter) — the
 * counterpart of {@link columnDraftKey} for the panels that are not columns.
 *
 * Same rule the ✕ applies (`GenericSidebarTemplate.ownDraftKey`): a panel whose own
 * id prop holds a create sentinel owns the type's active Entwurf, and a real record id
 * owns nothing — an edit and a contextual overlay must not throw away the parked
 * create draft that happens to share their type.
 *
 * Extracted because the ✕ was not the only way out. `GenericSidebar`'s click-outside
 * and the discard prompt behind it both close by calling `closeSidebar` directly, so
 * they skipped the discard entirely: a Verordnung dismissed that way left its `flow`
 * buffer standing, and the next „Verordnung erstellen" resumed inside the Fall the
 * user had walked away from instead of starting on the picker (TM-9502).
 */
export function sidebarDraftKey(sidebar: Sidebar<SidebarType> | null | undefined): DraftKey | null {
  if (!sidebar) return null;
  const type = sidebar.sidebarType;
  if (!draftDefinition(type)) return null;
  const idProp = idPropOf(type);
  const id = idProp
    ? (sidebar.props as Record<string, unknown> | null | undefined)?.[idProp]
    : undefined;
  return typeof id === 'string' && isDraftColumnId(id) ? activeDraftKey(type) : null;
}

/** Does this column hold unsaved work? See {@link columnDraftKey}. */
export function isColumnDraftState(stack: CascadeStack, column: CascadeColumn): boolean {
  if (!draftDefinition(column.type)) return false;
  const key = columnDraftKey(stack, column);
  if (key) return isDraftDirty(column.type, key);
  return isDraftColumnId(column.id) && isDraftDirty(column.type);
}

/**
 * The board a parked draft should come back to, which is not always the board on
 * screen.
 *
 * A create column opened FROM this one and left untouched — "Patient anlegen" from the
 * picker in "Programm zuweisen" — is in the URL right now, so recording the path
 * verbatim would resurrect it every time the rail is reopened. It holds nothing: a
 * pristine create column closes without a prompt and leaves no trace anywhere else, so
 * it must not be the one thing a stored path brings back.
 *
 * Only PRISTINE ones are dropped. A create column that holds something is a draft in
 * its own right and returns from the draft zone, not from this path.
 *
 * Ancestors of `index` are never dropped: closing that subtree would take the
 * recording column with it, and the path would then restore neither.
 */
export function boardForDraft(stack: CascadeStack, index: number): CascadeStack {
  const doomed = stack.findIndex(
    (candidate, i) =>
      i !== index &&
      isDraftColumnId(candidate.id) &&
      !!draftDefinition(candidate.type) &&
      !isColumnDraftState(stack, candidate) &&
      !isAncestor(stack, i, index),
  );
  return doomed === -1 ? stack : closeSubtree(stack, doomed);
}

/** Is the column at `ancestor` above the one at `index`? */
function isAncestor(stack: CascadeStack, ancestor: number, index: number): boolean {
  for (let p = stack[index]?.parent; p !== undefined; ) {
    if (p === ancestor) return true;
    p = stack[p]?.parent;
  }
  return false;
}
